import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, AlertCircle, CheckCircle2, GraduationCap, Download,
  CalendarClock, Printer, Stamp, PenLine, Activity, Upload,
} from "lucide-react";
import { BASE_URL } from "../../../features/api/apiSlice";
// The frame is shared with the status page: to a student these are one place,
// and two copies of the header would drift the first time either is touched.
import { PledgeShell as Shell, PledgeCard as Card, pledgeErrorMessage } from "../components/PledgeShell";

/**
 * The Graduating Students' Declaration of Intent — the student's whole
 * experience of the portal.
 *
 * Opened from a QR code on a departmental noticeboard by somebody who has
 * never used this portal and never will again. So: no login, no account, no
 * layout chrome, one screen, and every list it offers comes from the server
 * rather than being hard-coded here — the pilot's institutions change without
 * a frontend deploy.
 *
 * Fetched with a plain `fetch` rather than through the RTK api slice on
 * purpose: that slice attaches the stored bearer token and force-redirects to
 * /login on any 401. Neither belongs on a page whose whole point is working
 * without a session.
 *
 * What the student gets at the end is a Form No. and a PDF to print. That is
 * the product — the online form exists so that the paper the Secretariat
 * handles at the door is legible, complete and already in a spreadsheet.
 */

type Option = { value: string; label: string };

type FormMeta = {
  open: boolean;
  message?: string | null;
  cohort?: {
    id: number; name: string; year: number;
    ceremony_date: string | null; closes_on: string; closes_at: string;
  };
  institutions?: { id: number; name: string; acronym: string | null }[];
  programmes?: Option[];
  interest_areas?: Option[];
  states?: string[];
  graduation_years?: number[];
  declaration_text?: string;
  consent_text?: string;
};

type Receipt = {
  duplicate: boolean;
  form_no: string;
  name: string;
  institution: string;
  matric_no: string;
  programme: string;
  graduation: string;
  email: string;
  cohort: string | null;
  pdf_url: string;
  /** Their own record — offered here so they can bookmark it before closing the tab. */
  status_url: string;
  submitted_at: string | null;
};

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const EMPTY = {
  surname: "",
  other_names: "",
  institution_id: "",
  matric_no: "",
  programme: "",
  graduation_month: "",
  graduation_year: "",
  student_membership_no: "",
  not_yet_member: false,
  email: "",
  phone: "",
  alt_phone: "",
  state_after_graduation: "",
  interest_area: "",
  interest_other: "",
  declaration_accepted: false,
  data_consent: false,
};

type FormState = typeof EMPTY;

function fmtDate(iso?: string | null) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString("en-NG", {
    day: "numeric", month: "long", year: "numeric",
  });
}

/* ── Small presentational pieces ────────────────────────────────────────── */

function SectionHead({ letter, title, note }: { letter: string; title: string; note?: string }) {
  return (
    <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl">
      <div className="flex items-baseline gap-2">
        <span className="text-[10px] font-black uppercase tracking-widest text-[#0b2e6b]">
          Section {letter}
        </span>
        <span className="text-sm font-bold text-slate-800">{title}</span>
      </div>
      {note && <p className="text-[11px] text-slate-500 mt-0.5">{note}</p>}
    </div>
  );
}

function Field({
  label, required, error, hint, children,
}: {
  label: string; required?: boolean; error?: string; hint?: string; children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {hint && !error && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
      {error && <p className="text-[11px] text-red-600 mt-1">{error}</p>}
    </div>
  );
}

const inputCls =
  "w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-800 " +
  "focus:outline-none focus:ring-2 focus:ring-[#0b2e6b]/30 focus:border-[#0b2e6b] bg-white";

/** Radio group — a real <input type=radio> set, so it works without JS styling tricks. */
function RadioRow({
  name, options, value, onChange,
}: {
  name: string; options: Option[]; value: string; onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const active = value === o.value;
        return (
          <label
            key={o.value}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition
              ${active
                ? "border-[#0b2e6b] bg-[#0b2e6b]/5 text-[#0b2e6b] font-semibold"
                : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}
          >
            <input
              type="radio"
              name={name}
              value={o.value}
              checked={active}
              onChange={() => onChange(o.value)}
              className="accent-[#0b2e6b]"
            />
            {o.label}
          </label>
        );
      })}
    </div>
  );
}

/* ── Page ───────────────────────────────────────────────────────────────── */

export default function PublicGraduatePledgeFormPage() {
  const [meta, setMeta] = useState<FormMeta | null>(null);
  const [loadError, setLoadError] = useState("");
  const [loading, setLoading] = useState(true);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState<Receipt | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await fetch(`${BASE_URL}/graduate-pledge/form`, {
        headers: { Accept: "application/json" },
      });
      if (!res.ok) throw new Error(`The form could not be loaded (HTTP ${res.status}).`);
      const json = await res.json();
      setMeta(json?.data ?? null);
    } catch (e: any) {
      setLoadError(e?.message || "The form could not be loaded. Please check your connection.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    // Clear the server's complaint about a field the moment it is retyped —
    // leaving it under a box the student has already fixed reads as a bug.
    setErrors((e) => (e[key as string] ? { ...e, [key as string]: "" } : e));
  };

  const institutionOptions = meta?.institutions ?? [];
  const programmeOptions = meta?.programmes ?? [];
  const interestOptions = meta?.interest_areas ?? [];

  const closesOn = useMemo(() => fmtDate(meta?.cohort?.closes_on), [meta]);
  const ceremonyOn = useMemo(() => fmtDate(meta?.cohort?.ceremony_date), [meta]);

  /** The checks worth doing before a round trip. The server re-checks all of them. */
  const validate = (): boolean => {
    const e: Record<string, string> = {};

    if (!form.surname.trim()) e.surname = "Enter your surname.";
    if (!form.other_names.trim()) e.other_names = "Enter your other names.";
    if (!form.institution_id) e.institution_id = "Choose your institution.";
    if (!form.matric_no.trim()) e.matric_no = "Enter your matriculation number.";
    if (!form.programme) e.programme = "Choose your programme.";
    if (!form.graduation_month) e.graduation_month = "Choose the month.";
    if (!form.graduation_year) e.graduation_year = "Choose the year.";
    if (!form.not_yet_member && !form.student_membership_no.trim()) {
      e.student_membership_no =
        'Enter your NIQS student membership number, or tick "Not yet a member".';
    }
    if (!form.email.trim()) e.email = "Enter your email address.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      e.email = "That does not look like an email address.";
    }

    // Mirrors the server: 11 digits starting 0, or +234 / 234 followed by 10.
    const phoneOk = (v: string) => {
      const d = v.replace(/\D/g, "");
      return (d.length === 11 && d.startsWith("0")) || (d.length === 13 && d.startsWith("234"));
    };
    if (!form.phone.trim()) e.phone = "Enter your WhatsApp number.";
    else if (!phoneOk(form.phone)) e.phone = "11 digits starting 0, or +234…";
    if (form.alt_phone.trim() && !phoneOk(form.alt_phone)) {
      e.alt_phone = "11 digits starting 0, or +234…";
    }

    if (!form.state_after_graduation) e.state_after_graduation = "Choose where you expect to be.";
    if (!form.interest_area) e.interest_area = "Choose an area.";
    if (form.interest_area === "other" && !form.interest_other.trim()) {
      e.interest_other = "Say which area.";
    }
    if (!form.declaration_accepted) e.declaration_accepted = "Please confirm the Declaration.";
    if (!form.data_consent) e.data_consent = "Consent is required to submit this form.";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setSubmitError("");

    if (!validate()) {
      // Scroll to the first problem rather than leaving the student staring at
      // a Submit button that did nothing.
      const first = document.querySelector("[data-field-error='true']");
      first?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${BASE_URL}/graduate-pledge/submissions`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          ...form,
          institution_id: Number(form.institution_id),
          graduation_month: Number(form.graduation_month),
          graduation_year: Number(form.graduation_year),
          student_membership_no: form.not_yet_member ? null : form.student_membership_no.trim(),
          alt_phone: form.alt_phone.trim() || null,
          interest_other: form.interest_area === "other" ? form.interest_other.trim() : null,
        }),
      });

      const json = await res.json().catch(() => ({}));

      if (res.status === 422) {
        const fieldErrors: Record<string, string> = {};
        Object.entries(json?.errors ?? {}).forEach(([k, v]) => {
          fieldErrors[k] = Array.isArray(v) ? String(v[0]) : String(v);
        });
        setErrors(fieldErrors);
        setSubmitError(json?.message || "Please check the highlighted fields.");
        return;
      }

      if (!res.ok) {
        throw new Error(pledgeErrorMessage(json?.message, `Submission failed (HTTP ${res.status}).`));
      }

      setReceipt(json?.data ?? null);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e: any) {
      setSubmitError(e?.message || "Your submission could not be sent. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Loading / unavailable ────────────────────────────────────────────── */

  if (loading) {
    return (
      <Shell>
        <Card className="p-10 flex flex-col items-center gap-3">
          <Loader2 className="w-6 h-6 text-[#0b2e6b] animate-spin" />
          <p className="text-sm text-slate-500">Loading the form…</p>
        </Card>
      </Shell>
    );
  }

  if (loadError) {
    return (
      <Shell>
        <Card className="p-8 text-center space-y-4">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto" />
          <p className="text-sm text-slate-600">{loadError}</p>
          <button onClick={load} className="text-sm font-semibold text-[#0b2e6b] underline">
            Try again
          </button>
        </Card>
      </Shell>
    );
  }

  if (!meta?.open) {
    return (
      <Shell>
        <Card className="p-8 text-center space-y-3">
          <CalendarClock className="w-8 h-8 text-amber-500 mx-auto" />
          <h2 className="text-base font-bold text-slate-800">This form is closed</h2>
          <p className="text-sm text-slate-600 max-w-md mx-auto leading-relaxed">
            {meta?.message ||
              "The Graduate Pledge form is not accepting submissions at the moment."}
          </p>
          {/* Most people who reach a closed form already submitted weeks ago
              and are here to check on it, not to submit again. */}
          <p className="text-xs text-slate-500 pt-2">
            Already submitted?{" "}
            <Link to="/graduate-pledge/status" className="text-[#0b2e6b] underline font-semibold">
              Check your status
            </Link>
          </p>
        </Card>
      </Shell>
    );
  }

  /* ── Receipt ──────────────────────────────────────────────────────────── */

  if (receipt) {
    return (
      <Shell>
        <Card className="overflow-hidden">
          <div className="bg-emerald-50 border-b border-emerald-200 px-6 py-5 text-center">
            <CheckCircle2 className="w-9 h-9 text-emerald-600 mx-auto mb-2" />
            <h2 className="text-base font-bold text-emerald-900">
              {receipt.duplicate ? "You have already submitted this form" : "Declaration received"}
            </h2>
            <p className="text-xs text-emerald-800 mt-1">
              A copy has been emailed to {receipt.email}.
            </p>
          </div>

          <div className="px-6 py-6 space-y-6">
            <div className="text-center">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                Your Form No.
              </div>
              <div className="text-2xl font-black text-[#0b2e6b] tracking-wide mt-1">
                {receipt.form_no}
              </div>
            </div>

            <div className="space-y-2">
              <a
                href={receipt.pdf_url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-center gap-2 w-full rounded-lg bg-[#0b2e6b] text-white
                           px-4 py-3 text-sm font-bold hover:bg-[#0a2558]"
              >
                <Download className="w-4 h-4" />
                Download my form (PDF)
              </a>
              {/* The same link as in their email. Offered here because this is
                  the one moment we know they are looking — after today the
                  email is the only way back, and emails get lost. */}
              <a
                href={receipt.status_url}
                className="flex items-center justify-center gap-2 w-full rounded-lg border
                           border-slate-300 px-4 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50"
              >
                <Activity className="w-4 h-4" />
                Upload my signed form &amp; check my status
              </a>
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-3">
                What to do next
              </div>
              <ol className="space-y-2.5 text-sm text-slate-700">
                <li className="flex gap-2.5">
                  <Printer className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                  Print the form.
                </li>
                <li className="flex gap-2.5">
                  <PenLine className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                  Sign Sections C and D by hand.
                </li>
                <li className="flex gap-2.5">
                  <Stamp className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                  Get your Head of Department&rsquo;s signature and stamp in Section E.
                </li>
                {/* The step that sends it back. Named here because this list is
                    the only instruction most students will read — an upload
                    reachable solely from a status link nobody is told about is
                    an upload that never happens. */}
                <li className="flex gap-2.5">
                  <Upload className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                  <span>
                    <a
                      href={receipt.status_url}
                      className="text-[#0b2e6b] underline font-semibold"
                    >
                      Upload a photo of the signed form
                    </a>{" "}
                    so the Secretariat can check it before the day.
                  </span>
                </li>
                <li className="flex gap-2.5">
                  <GraduationCap className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" />
                  Bring the signed printout to the Pledge Ceremony
                  {ceremonyOn ? ` on ${ceremonyOn}` : ""} &mdash; the Institute
                  collects the original.
                </li>
              </ol>
            </div>

            <dl className="text-sm divide-y divide-slate-100">
              {[
                ["Name", receipt.name],
                ["Institution", receipt.institution],
                ["Matriculation no.", receipt.matric_no],
                ["Programme", receipt.programme],
                ["Expected graduation", receipt.graduation],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 py-2">
                  <dt className="text-slate-400">{k}</dt>
                  <dd className="font-semibold text-slate-800 text-right">{v}</dd>
                </div>
              ))}
            </dl>

            <p className="text-[11px] text-slate-500 leading-relaxed border-t border-slate-100 pt-4">
              If any detail is wrong, email the Secretariat quoting your Form No. rather than
              filling the form in again. Completing this form does not by itself make you a
              member of the Institute.
            </p>
          </div>
        </Card>
      </Shell>
    );
  }

  /* ── The form ─────────────────────────────────────────────────────────── */

  const err = (k: string) => errors[k] || "";
  const flag = (k: string) => (errors[k] ? { "data-field-error": "true" } : {});

  return (
    <Shell>
      <form onSubmit={submit} className="space-y-5" noValidate>

        <Card className="px-5 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm font-bold text-slate-800">{meta.cohort?.name}</div>
              {ceremonyOn && (
                <div className="text-xs text-slate-500">Pledge Ceremony: {ceremonyOn}</div>
              )}
            </div>
            {closesOn && (
              <div className="flex items-center gap-2 text-xs font-semibold text-amber-700
                              bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                <CalendarClock className="w-3.5 h-3.5" />
                Closes {closesOn}, 23:59
              </div>
            )}
          </div>
        </Card>

        {/* ── Section A ── */}
        <Card>
          <SectionHead letter="A" title="Your particulars" />
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div {...flag("surname")}>
              <Field label="Surname" required error={err("surname")}>
                <input
                  className={inputCls} value={form.surname}
                  onChange={(e) => set("surname", e.target.value)}
                  autoComplete="family-name"
                />
              </Field>
            </div>

            <div {...flag("other_names")}>
              <Field label="Other names" required error={err("other_names")}>
                <input
                  className={inputCls} value={form.other_names}
                  onChange={(e) => set("other_names", e.target.value)}
                  autoComplete="given-name"
                />
              </Field>
            </div>

            <div className="sm:col-span-2" {...flag("institution_id")}>
              <Field label="Institution" required error={err("institution_id")}>
                <select
                  className={inputCls}
                  value={form.institution_id}
                  onChange={(e) => set("institution_id", e.target.value)}
                >
                  <option value="">Select your institution…</option>
                  {institutionOptions.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}{i.acronym ? ` (${i.acronym})` : ""}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div {...flag("matric_no")}>
              <Field label="Matriculation number" required error={err("matric_no")}>
                <input
                  className={inputCls} value={form.matric_no}
                  onChange={(e) => set("matric_no", e.target.value)}
                />
              </Field>
            </div>

            <div {...flag("programme")}>
              <Field label="Programme" required error={err("programme")}>
                <RadioRow
                  name="programme"
                  options={programmeOptions}
                  value={form.programme}
                  onChange={(v) => set("programme", v)}
                />
              </Field>
            </div>

            <div className="sm:col-span-2" {...flag("graduation_month")}>
              <Field label="Expected graduation" required
                     error={err("graduation_month") || err("graduation_year")}>
                <div className="grid grid-cols-2 gap-3">
                  <select
                    className={inputCls}
                    value={form.graduation_month}
                    onChange={(e) => set("graduation_month", e.target.value)}
                  >
                    <option value="">Month…</option>
                    {MONTHS.map((m, i) => (
                      <option key={m} value={i + 1}>{m}</option>
                    ))}
                  </select>
                  <select
                    className={inputCls}
                    value={form.graduation_year}
                    onChange={(e) => set("graduation_year", e.target.value)}
                  >
                    <option value="">Year…</option>
                    {(meta.graduation_years ?? []).map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>
              </Field>
            </div>

            <div className="sm:col-span-2" {...flag("student_membership_no")}>
              <Field
                label="NIQS student membership no."
                error={err("student_membership_no")}
                hint="Leave blank and tick the box below if you are not a student member yet."
              >
                <input
                  className={`${inputCls} disabled:bg-slate-100 disabled:text-slate-400`}
                  value={form.student_membership_no}
                  disabled={form.not_yet_member}
                  onChange={(e) => set("student_membership_no", e.target.value)}
                />
              </Field>
              <label className="flex items-center gap-2 mt-2 text-sm text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-[#0b2e6b] w-4 h-4"
                  checked={form.not_yet_member}
                  onChange={(e) => {
                    set("not_yet_member", e.target.checked);
                    if (e.target.checked) set("student_membership_no", "");
                  }}
                />
                Not yet a member
              </label>
            </div>
          </div>
        </Card>

        {/* ── Section B ── */}
        <Card>
          <SectionHead letter="B" title="Contact details and intentions" />
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2" {...flag("email")}>
              <Field label="Email address" required error={err("email")}
                     hint="Your form will be emailed here. Use an address you will still have after graduation.">
                <input
                  type="email" className={inputCls} value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  autoComplete="email"
                />
              </Field>
            </div>

            <div {...flag("phone")}>
              <Field label="Phone (WhatsApp)" required error={err("phone")} hint="e.g. 08031234567">
                <input
                  type="tel" className={inputCls} value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  autoComplete="tel"
                />
              </Field>
            </div>

            <div {...flag("alt_phone")}>
              <Field label="Alternative phone" error={err("alt_phone")}>
                <input
                  type="tel" className={inputCls} value={form.alt_phone}
                  onChange={(e) => set("alt_phone", e.target.value)}
                />
              </Field>
            </div>

            <div className="sm:col-span-2" {...flag("state_after_graduation")}>
              <Field label="State you expect to be in after graduation" required
                     error={err("state_after_graduation")}>
                <select
                  className={inputCls}
                  value={form.state_after_graduation}
                  onChange={(e) => set("state_after_graduation", e.target.value)}
                >
                  <option value="">Select…</option>
                  {(meta.states ?? []).map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="sm:col-span-2" {...flag("interest_area")}>
              <Field label="Area of QS interest" required error={err("interest_area")}>
                <RadioRow
                  name="interest_area"
                  options={interestOptions}
                  value={form.interest_area}
                  onChange={(v) => set("interest_area", v)}
                />
              </Field>
              {form.interest_area === "other" && (
                <div className="mt-3" {...flag("interest_other")}>
                  <Field label="Please specify" required error={err("interest_other")}>
                    <input
                      className={inputCls} value={form.interest_other}
                      onChange={(e) => set("interest_other", e.target.value)}
                    />
                  </Field>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* ── Section C ── */}
        <Card>
          <SectionHead
            letter="C" title="Declaration of Intent"
            note="You will sign this by hand at the ceremony."
          />
          <div className="p-5 space-y-4">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm
                            text-slate-700 whitespace-pre-line leading-relaxed">
              {meta.declaration_text}
            </div>
            <div {...flag("declaration_accepted")}>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-[#0b2e6b] w-4 h-4 mt-0.5 shrink-0"
                  checked={form.declaration_accepted}
                  onChange={(e) => set("declaration_accepted", e.target.checked)}
                />
                <span className="text-sm text-slate-700">
                  I have read this Declaration and will sign it at the ceremony.
                  <span className="text-red-500"> *</span>
                </span>
              </label>
              {err("declaration_accepted") && (
                <p className="text-[11px] text-red-600 mt-1">{err("declaration_accepted")}</p>
              )}
            </div>
          </div>
        </Card>

        {/* ── Section D ── */}
        <Card>
          <SectionHead letter="D" title="Data protection consent" />
          <div className="p-5 space-y-4">
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-[13px]
                            text-slate-700 leading-relaxed">
              {meta.consent_text}
            </div>
            <div {...flag("data_consent")}>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="accent-[#0b2e6b] w-4 h-4 mt-0.5 shrink-0"
                  checked={form.data_consent}
                  onChange={(e) => set("data_consent", e.target.checked)}
                />
                <span className="text-sm text-slate-700">
                  I give my consent as stated above.
                  <span className="text-red-500"> *</span>
                </span>
              </label>
              {err("data_consent") && (
                <p className="text-[11px] text-red-600 mt-1">{err("data_consent")}</p>
              )}
            </div>
          </div>
        </Card>

        {submitError && (
          <div className="flex items-start gap-2 px-4 py-3 rounded-lg border border-red-200
                          bg-red-50 text-sm text-red-700">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{submitError}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-lg bg-[#0b2e6b] text-white px-4 py-3.5 text-sm font-bold
                     hover:bg-[#0a2558] disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {submitting ? "Submitting…" : "Submit my declaration"}
        </button>

        <p className="text-center text-[11px] text-slate-400">
          Sections C, D and E are signed by hand on the printout. Submitting here does not
          by itself make you a member of the Institute.
        </p>

        {/* Submitting again is harmless — it returns the existing Form No. and
            re-sends the PDF — but it is a long way round for someone who only
            wants to know where they stand. */}
        <p className="text-center text-[11px] text-slate-400">
          Already submitted?{" "}
          <Link to="/graduate-pledge/status" className="text-[#0b2e6b] underline font-semibold">
            Check your status
          </Link>
        </p>
      </form>
    </Shell>
  );
}
